import { Dispatch, SetStateAction, useState } from "react"

import { usePostIntegrationConsentConfirmedEvent } from "src/data/analytics/queries/integrationAnalyticsQueries"
import {
  useConnectOauth,
  useFetchActiveCredentials,
} from "src/data/integrations/queries/integrationAuthQueries"
import {
  OwnerType,
  TIntegration,
} from "src/data/integrations/types/integrationTypes"
import { Routes } from "src/router/routes"

import { BeforeYouContinueDialog } from "./BeforeYouContinueDialog"
import { ClientCredentialDialog } from "./ClientCredentialDialog"
import { MultiSecretDialog } from "./MultiSecretDialog"
import { SecretDialog } from "./SecretDialog"

export function IntegrationConnectDialogs({
  integration,
  orgId,
  setStartAuthFlow,
  startAuthFlow,
  reauthorizationAccountId,
}: {
  integration: TIntegration
  orgId: string
  setStartAuthFlow: Dispatch<SetStateAction<boolean>>
  startAuthFlow: boolean
  reauthorizationAccountId?: string
}) {
  const connectOauth = useConnectOauth({ ownerType: OwnerType.ORGANIZATION })
  const [redirecting, setRedirecting] = useState(false)
  const [showSecretDialog, setShowSecretDialog] = useState(false)
  const [showMultiSecretDialog, setShowMultiSecretModal] = useState(false)
  const [showClientCredModal, setShowClientCredDialog] = useState(false)
  const reauthorizationRequired = !!reauthorizationAccountId

  const fetchActiveCredentials = useFetchActiveCredentials({
    ownerId: orgId,
    ownerType: OwnerType.ORGANIZATION,
    integration: integration.integration_identifier,
    filters: { limit: 1, offset: 0 },
  })

  const isMultiAccountIntegration =
    (fetchActiveCredentials.data?.paging?.total_count ?? 0) > 0

  const settingsTabLocation = Routes.OrgIntegrationSettingsTab.location(
    integration.integration_identifier
  )

  const homeTabLocation = Routes.OrgIntegration.location(
    integration.integration_identifier
  )

  const integrationRedirectPath = isMultiAccountIntegration
    ? `${settingsTabLocation.pathname}#${settingsTabLocation.hash}`
    : homeTabLocation.pathname

  const redirectUrl = window.location.origin + integrationRedirectPath

  const postIntegrationConsentConfirmedEvent =
    usePostIntegrationConsentConfirmedEvent()

  function handleSeamFlow() {
    // Seam mimics Oauth but works a bit differently, so it has its own url
    startOauthFlow("/integrations/auth/seam_connect_webviews/connect")
  }

  function startOauthFlow(url?: string) {
    setRedirecting(true)
    connectOauth.mutate(
      {
        orgId,
        redirectUrl: redirectUrl, // TODO WEB-xxx: Use react-router instead?
        integration: integration.integration_identifier,
        reauthorizeAccountId: reauthorizationAccountId,
        url,
      },
      {
        onError: () => {
          setRedirecting(false)
        },
      }
    )
  }

  function handleOauthFlow() {
    startOauthFlow()
  }

  function handleShowMultiSecretDialog() {
    setShowMultiSecretModal(true)
    setStartAuthFlow(false)
  }

  function handleShowSecretDialog() {
    setShowSecretDialog(true)
    setStartAuthFlow(false)
  }

  function handleClientCredentials() {
    setShowClientCredDialog(true)
    setStartAuthFlow(false)
  }

  function handleAgreedPrivacy() {
    postIntegrationConsentConfirmedEvent.mutate({
      integration_name: integration.name,
      reconnect: reauthorizationRequired,
    })

    switch (integration.auth.type) {
      case "SECRET":
        return handleShowSecretDialog()
      case "MULTI_SECRET":
        return handleShowMultiSecretDialog()
      case "OAUTH2":
        return handleOauthFlow()
      case "OAUTH2_CLIENT_CREDENTIALS":
        return handleClientCredentials()
      case "SEAM_CONNECT_WEBVIEW":
        return handleSeamFlow()
    }
  }

  return (
    <>
      <BeforeYouContinueDialog
        name={integration.name}
        integrationId={integration.integration_identifier}
        open={startAuthFlow}
        onClose={() => setStartAuthFlow(false)}
        onConfirm={handleAgreedPrivacy}
        loading={redirecting}
        type={integration.type}
      />
      <ClientCredentialDialog
        integration={integration}
        open={showClientCredModal}
        onClose={() => setShowClientCredDialog(false)}
        orgId={orgId}
        ownerType={OwnerType.ORGANIZATION}
        reauthorize={reauthorizationRequired}
      />
      <SecretDialog
        integration={integration}
        open={showSecretDialog}
        onClose={() => setShowSecretDialog(false)}
        orgId={orgId}
        ownerType={OwnerType.ORGANIZATION}
        reauthorize={reauthorizationRequired}
      />
      <MultiSecretDialog
        integration={integration}
        open={showMultiSecretDialog}
        onClose={() => setShowMultiSecretModal(false)}
        orgId={orgId}
        ownerType={OwnerType.ORGANIZATION}
        reauthorize={reauthorizationRequired}
      />
    </>
  )
}
